import { useEffect, useState } from 'react';
import ShellLink from '../common/ShellLink';
import api from '../../services/api';
import Drawer from '../common/Drawer';
import { useEntitlements } from '../../contexts/EntitlementContext';
import type { BoardroomSummary, LinkedRoom } from '../../types';

/**
 * Which boardroom this subject reads.
 *
 * THE CHOICE IS MADE HERE, on the subject, because the subject is the side that
 * asks the question — you are looking at a thing you are about to write about
 * and deciding what research should back it.
 *
 * Several subjects may read the same room, and that is the ordinary case rather
 * than an edge one: a room maps a market, and a market is usually worth more
 * than one subject. Linking here never takes the room away from anywhere else.
 *
 * WHAT THE LINK DOES, and why it is worth saying on screen: while it is set,
 * this subject reads that room LIVE. Nothing is copied. A page written under
 * this subject can ask what the room found and sees it as it is now, not as it
 * was when the link was made.
 *
 * LINKED HAS TO LOOK LINKED: a chip with a colour and a mark. Unlinked stays
 * quiet, because an invitation should be quieter than a fact.
 *
 * A DRAWER, NOT A DROPDOWN: an account holds many rooms with repeated names, so
 * a search box and rows that say what each room HOLDS. The linked room is pinned
 * above the search, because the searched list cannot be trusted to contain it.
 *
 * Only offered to someone who holds Boardroom — but a link that already exists
 * is always shown, so it can still be removed.
 */

interface Props {
  projectId: number;
  /** The linked room's id, or null. Owned by the parent so the page can react. */
  boardroomId: number | null;
  onChange: (boardroomId: number | null) => void;
  /** Optional: lets the page open the picker from elsewhere (the empty
   *  subject's first step). Left unset, the control keeps its own state. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export default function RoomLink({
  projectId,
  boardroomId,
  onChange,
  open: openProp,
  onOpenChange,
}: Props) {
  const { has } = useEntitlements();
  const [openOwn, setOpenOwn] = useState(false);
  const open = openProp ?? openOwn;
  const setOpen = (next: boolean) => {
    setOpenOwn(next);
    onOpenChange?.(next);
  };
  // The linked room itself — the subject row carries only an id, and the name
  // plus what it holds is what makes the link legible.
  const [linked, setLinked] = useState<LinkedRoom | null>(null);

  useEffect(() => {
    if (!boardroomId) {
      setLinked(null);
      return;
    }
    let live = true;
    api
      .getRoomForSubject(projectId)
      .then((r) => live && setLinked(r))
      // Silent: failing to name the room must not break the control that can
      // still unlink it. The trigger renders regardless.
      .catch(() => live && setLinked(null));
    return () => {
      live = false;
    };
  }, [projectId, boardroomId]);

  if (!boardroomId && !has('BOARDROOM')) return null;

  return (
    <>
      {boardroomId ? (
        <button
          onClick={() => setOpen(true)}
          className="inline-flex max-w-[16rem] items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-800 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-900/40 dark:text-blue-200 dark:hover:bg-blue-900/70"
          title={`Pages written under this subject read ${
            linked?.name ?? 'this room'
          } live. Click to change or remove the link.`}
        >
          <svg
            className="h-3.5 w-3.5 shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.5 1.5" />
            <path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.5-1.5" />
          </svg>
          <span className="truncate">Reads {linked?.name ?? 'a room'}</span>
        </button>
      ) : (
        <button
          onClick={() => setOpen(true)}
          className="px-2 py-1 text-xs rounded text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-200"
          title="Let pages written under this subject read what a recon found"
        >
          Link a room
        </button>
      )}

      {open && (
        <RoomPicker
          projectId={projectId}
          boardroomId={boardroomId}
          linked={linked}
          onClose={() => setOpen(false)}
          onChange={(id) => onChange(id)}
        />
      )}
    </>
  );
}

function RoomPicker({
  projectId,
  boardroomId,
  linked,
  onClose,
  onChange,
}: {
  projectId: number;
  boardroomId: number | null;
  /** What is linked right now, if the lookup answered. Pinned, not searched. */
  linked: LinkedRoom | null;
  onClose: () => void;
  onChange: (boardroomId: number | null) => void;
}) {
  const [rooms, setRooms] = useState<BoardroomSummary[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Searched SERVER-side: filtering only the page you happen to be holding would
  // miss the room you are looking for.
  useEffect(() => {
    let live = true;
    setLoading(true);
    const t = setTimeout(() => {
      api
        .listBoardrooms({ limit: 50, q: q.trim() || null })
        .then((page) => live && setRooms(page.items))
        .catch(
          (e) =>
            live && setError(e instanceof Error ? e.message : 'Could not load your rooms'),
        )
        .finally(() => live && setLoading(false));
    }, 200);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [q]);

  const pick = async (next: BoardroomSummary | null) => {
    setBusy(true);
    setError('');
    try {
      // `null` UNLINKS: the endpoint dumps with exclude_unset, so an explicit
      // null and an omitted field are already two different instructions.
      const updated = await api.updateProject(projectId, {
        boardroom_id: next ? next.id : null,
      });
      onChange(updated.boardroom_id ?? null);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change the link');
      setBusy(false);
    }
  };

  const held = (linked?.sections ?? []).filter((s) => s.count > 0);

  return (
    <Drawer
      onClose={onClose}
      side="right"
      width="w-[38rem]"
      eyebrow="Subject"
      title="Which room feeds this subject"
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Pages written under this subject can ask what the room found - what buyers
          search for, the competitors, the problems people complain about and where
          they said it. It is read <strong>live</strong>: nothing is copied, so the
          room can keep digging after you link it.
        </p>

        {boardroomId ? (
          <div className="rounded-lg border border-blue-300 bg-blue-50 p-3 dark:border-blue-700 dark:bg-blue-900/30">
            <div className="text-[11px] font-medium uppercase tracking-wide text-blue-700 dark:text-blue-300">
              Linked now
            </div>
            <div className="mt-1 text-sm font-medium text-gray-900 break-words dark:text-gray-100">
              {linked?.name ?? 'This room could not be loaded'}
            </div>
            {held.length > 0 && (
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
                {held.map((s, i) => (
                  <span key={s.kind} className="whitespace-nowrap">
                    <span className="font-medium tabular-nums">{s.count}</span>{' '}
                    {s.label.toLowerCase()}
                    {i < held.length - 1 && (
                      <span className="text-gray-300 dark:text-gray-600"> ·</span>
                    )}
                  </span>
                ))}
              </div>
            )}
            <div className="mt-2.5 flex items-center gap-2">
              <button
                disabled={busy}
                onClick={() => pick(null)}
                className="rounded border border-red-300 px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/30"
              >
                Unlink this room
              </button>
              {linked && (
                <ShellLink
                  to={`/boardroom/${linked.boardroom_id}`}
                  className="rounded border border-gray-300 px-2.5 py-1 text-xs text-gray-700 hover:bg-white dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  Open the room
                </ShellLink>
              )}
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Unlinking stops pages here reading it. It changes nothing in the
                room.
              </span>
            </div>
          </div>
        ) : (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            This subject reads no room yet. More than one subject can read the
            same room - linking here does not take it away from anywhere else.
          </p>
        )}

        {error && (
          <p className="text-xs text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        )}

        <div>
          <div className="mb-2 text-xs font-medium text-gray-700 dark:text-gray-300">
            {boardroomId ? 'Read a different room instead' : 'Pick a room'}
          </div>
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search rooms…"
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded"
          />
        </div>

        {loading && (
          <p className="text-sm text-gray-400 dark:text-gray-500">Loading rooms…</p>
        )}

        {!loading && rooms.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {q.trim() ? (
              <>No room matches “{q.trim()}”.</>
            ) : (
              <>
                You have no recons yet.{' '}
                <ShellLink to="/boardroom" className="text-blue-700 dark:text-blue-300 hover:underline">
                  Open one
                </ShellLink>
                .
              </>
            )}
          </p>
        )}

        <div className="space-y-1">
          {rooms.map((r) => {
            const isLinked = r.id === boardroomId;
            return (
              <button
                key={r.id}
                disabled={busy}
                onClick={() => (isLinked ? onClose() : pick(r))}
                className={`w-full text-left px-3 py-2 rounded border disabled:opacity-50 ${
                  isLinked
                    ? 'border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                    : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                <div className="flex items-start gap-2">
                  <span className="flex-1 min-w-0 text-sm font-medium text-gray-900 dark:text-gray-100 break-words">
                    {r.name}
                  </span>
                  {isLinked && (
                    <span className="shrink-0 text-[11px] font-medium text-blue-700 dark:text-blue-300">
                      Linked
                    </span>
                  )}
                </div>
                {/* What the room was opened to answer — often the only thing
                    separating two rooms with the same name. Clamped. */}
                {r.description && (
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                    {r.description}
                  </p>
                )}
                <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                  <span>
                    <span className="font-medium tabular-nums">{r.items_created}</span> items
                  </span>
                  <span className="text-gray-300 dark:text-gray-600">·</span>
                  <span>
                    <span className="font-medium tabular-nums">{r.runs_done}</span> digs
                  </span>
                  <span className="ml-auto shrink-0 text-gray-400 dark:text-gray-500">
                    {new Date(r.updated_at).toLocaleDateString()}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </Drawer>
  );
}
